import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { drawnPoints } from '@/lib/aggregate';

export type TrendUnit = 'percent' | 'count';

export interface TrendPoint {
  /** X軸ラベル（'2026-07' または '2026 Q3'） */
  period: string;
  value: number | null;
}

interface TrendChartProps {
  data: TrendPoint[];
  /** ツールチップに表示する指標名（Y軸ラベルには入れない。単位は unit から自動付与） */
  yLabel: string;
  /** X軸に表示する期間の種別 */
  xLabel: string;
  unit: TrendUnit;
  /** Y軸ラベルに添える集約方法（'四半期合計' 等）。月次など素の値のときは省略 */
  aggregation?: string | null;
  color?: string;
  height?: number;
}

/** @graph-connects none */
function formatValue(value: number, unit: TrendUnit): string {
  return unit === 'percent' ? `${(value * 100).toFixed(1)}%` : value.toLocaleString();
}

/** Y軸の目盛り。percent は 0..1 を % 表記にする */
/** @graph-connects none */
function formatTick(value: number, unit: TrendUnit): string {
  return unit === 'percent' ? `${(value * 100).toFixed(0)}%` : value.toLocaleString();
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business KPIの期間推移チャート。X軸(期間)・Y軸(指標と単位)のラベルを明示し、月次/四半期の切替表示に対応する
 * @graph-connects none
 */
export function TrendChart({
  data,
  yLabel,
  xLabel,
  unit,
  aggregation,
  color = 'var(--color-primary)',
  height = 200,
}: TrendChartProps) {
  const filtered = drawnPoints(data);
  if (filtered.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs"
        style={{ height, color: 'var(--color-text-muted)' }}
      >
        データがありません
      </div>
    );
  }

  const gradientId = `trend-gradient-${yLabel.replace(/[^a-zA-Z0-9]/g, '')}-${unit}`;
  const unitLabel = unit === 'percent' ? '%' : '件';
  // Y軸ラベルは回転表示なので、長さの上限はプロット領域の「高さ」になる。
  // 指標名は呼び出し側のカード見出しに必ず出ているため、軸には単位と集約だけを置く。
  // （指標名まで入れると 176px のカードで確実にはみ出す）
  const axisLabel = aggregation ? `${unitLabel}・${aggregation}` : unitLabel;

  return (
    <div role="img" aria-label={`${yLabel}の推移（${xLabel}）`}>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={filtered} margin={{ top: 8, right: 12, bottom: 28, left: 8 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0.04} />
            </linearGradient>
          </defs>

          <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" vertical={false} />

          <XAxis
            dataKey="period"
            tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
            tickLine={false}
            axisLine={{ stroke: 'var(--color-border)' }}
            interval="preserveStartEnd"
            minTickGap={12}
            label={{
              value: xLabel,
              position: 'insideBottom',
              offset: -18,
              style: { fontSize: 10, fill: 'var(--color-text-secondary)' },
            }}
          />

          <YAxis
            tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
            tickLine={false}
            axisLine={{ stroke: 'var(--color-border)' }}
            width={52}
            domain={['auto', 'auto']}
            tickFormatter={(v: number) => formatTick(v, unit)}
            label={{
              value: axisLabel,
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 10, fill: 'var(--color-text-secondary)', textAnchor: 'middle' },
            }}
          />

          <Tooltip
            contentStyle={{
              background: 'var(--color-bg-elevated)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            labelStyle={{ color: 'var(--color-text-secondary)' }}
            formatter={(value) => [
              typeof value === 'number' ? formatValue(value, unit) : '—',
              yLabel,
            ]}
          />

          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={{ r: 2, fill: color }}
            activeDot={{ r: 4 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
